import { useApolloClient } from '@apollo/client';
import { extractDataFromResponse, useErrorReporter } from '@mdlinx/frontend-shared';
import { failed, Result, succeeded } from '@mdlinx/shared';
import { OAuth } from '@mdlinx/shared/dist';
import { defaults } from '@mdlinx/shared/dist/defaults';

import {
  getAuth,
  signInWithPopup,
  FacebookAuthProvider,
  GoogleAuthProvider,
  AuthProvider,
  UserCredential,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import {
  LoginWithPasswordDocument,
  LoginWithPasswordError,
  LoginWithRefreshTokenDocument,
  LoginWithRefreshTokenMutation,
  LoginWithRefreshTokenMutationVariables,
  ResetPasswordResult,
  useResetPasswordMutation,
  useSendEmailVerificationMutation,
  SendEmailVerificationError as GQLSendEmailVerificationError,
} from '../generated/client';
import { useFirebaseApp } from './firebaseApp';
import { RegistrationTrackingData } from './tracking';

type PasswordLoginResult = { success: true } | { success: false; error: LoginWithPasswordError };

export function useLoginWithPassword(): (params: { email: string; password: string }) => Promise<PasswordLoginResult> {
  const apolloClient = useApolloClient();
  const errorReporter = useErrorReporter();

  return async ({ email, password }) => {
    const response = await apolloClient.mutate({
      mutation: LoginWithPasswordDocument,
      variables: {
        input: {
          email,
          password,
        },
      },
    });
    const data = extractDataFromResponse(response);
    const result = data.loginWithPassword;

    switch (result.__typename) {
      case 'LoginWithPasswordSuccess':
        return { success: true };
      case 'LoginWithPasswordFailure':
        return { success: false, error: data.loginWithPassword.error };
      default:
        errorReporter.report(new Error(`__typename should be specified in return value: ${result.__typename}`));
        return { success: false, error: LoginWithPasswordError.Unknown };
    }
  };
}

type SocialLoginErrorOr<T> = Result<SocialLoginError, T>;

export type SocialLoginResult = SocialLoginErrorOr<{ credential?: UserCredential }>;

export type SocialLoginError =
  | 'POPUP_BLOCKED'
  | 'POPUP_CLOSED'
  | 'CANCELED'
  | 'STATE_UNMATCHED'
  | 'ANOTHER_ACCOUNT_EXISTS'
  | 'UNKNOWN';

const facebookAuthProvider = new FacebookAuthProvider();
export function useLoginWithFacebookAccount({
  registrationTrackingData,
}: {
  registrationTrackingData: RegistrationTrackingData;
}): () => Promise<SocialLoginResult> {
  return useLoginWithSocialAccount({
    registrationTrackingData,
    provider: facebookAuthProvider,
  });
}

function popupFeatures(): string {
  const width = 500;
  const height = 600;
  const top = (window.screen.availHeight - height) / 2;
  const left = (window.screen.availWidth - width) / 2;
  return Object.entries({
    width,
    height,
    top: top > 0 ? top : 0,
    left: left > 0 ? left : 0,
    location: 'yes',
    resizable: 'yes',
    statusbar: 'yes',
    toolbar: 'no',
    menubar: 'no',
  })
    .map(([k, v]) => `${k}=${v}`)
    .join(',');
}

export function useLoginWithLinkedInAccount({
  registrationTrackingData,
}: {
  registrationTrackingData: RegistrationTrackingData;
}): () => Promise<SocialLoginResult> {
  const popupBaseUrl =
    process.env.NODE_ENV === 'development'
      ? `http://localhost:${defaults.api.auth.port}`
      : `https://auth-api.${process.env.REACT_APP_ROOT_DOMAIN_NAME}`;
  const popupUrl = `${popupBaseUrl}/oauth/linkedin/redirect?${new URLSearchParams(
    registrationTrackingData,
  ).toString()}`;

  return async (): Promise<SocialLoginResult> => {
    const maybePopup = window.open(popupUrl, 'MDLinx LinkedIn Login', popupFeatures());
    if (!maybePopup) {
      return failed('POPUP_BLOCKED');
    }
    const popup = maybePopup;
    return new Promise<SocialLoginResult>(resolve => {
      window.addEventListener('message', handleMessage);
      const popupStatusCheck = setInterval(() => {
        if (popup.closed) {
          finalize(failed('POPUP_CLOSED'));
        }
      }, 1000);

      function oauthResultToLoginResult(result: OAuth.Result): SocialLoginResult {
        switch (result) {
          case OAuth.loginSucceeded:
            return succeeded({});
          case OAuth.canceled:
            return failed('CANCELED');
          case OAuth.anotherAccountExists:
            return failed('ANOTHER_ACCOUNT_EXISTS');
          case OAuth.stateUnmatched:
            return failed('STATE_UNMATCHED');
          case OAuth.unknown:
            return failed('UNKNOWN');
        }
      }

      function handleMessage({ data }: MessageEvent) {
        if (OAuth.isOAuthResult(data)) {
          finalize(oauthResultToLoginResult(data));
        }
      }

      function finalize(result: SocialLoginResult) {
        window.removeEventListener('message', handleMessage);
        clearInterval(popupStatusCheck);
        if (!popup.closed) {
          popup.close();
        }
        resolve(result);
      }
    });
  };
}

const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.addScope('email');

export function useLoginWithGoogleAccount({
  registrationTrackingData,
}: {
  registrationTrackingData: RegistrationTrackingData;
}): () => Promise<SocialLoginResult> {
  return useLoginWithSocialAccount({ registrationTrackingData, provider: googleAuthProvider });
}

function useLoginWithSocialAccount({
  registrationTrackingData,
  provider,
}: {
  registrationTrackingData: RegistrationTrackingData;
  provider: AuthProvider;
}): () => Promise<SocialLoginResult> {
  const errorReporter = useErrorReporter();
  const firebaseApp = useFirebaseApp();
  const apolloClient = useApolloClient();

  async function loginAsFirebaseUser(): Promise<
    SocialLoginErrorOr<{ refreshToken: string; credential: UserCredential }>
  > {
    return signInWithPopup(getAuth(firebaseApp), provider).then(
      result => {
        if (!result.user) {
          errorReporter.report(
            new Error(
              `user does not exist after "signInWithPopup" for provider: ${provider.providerId}, even though sign-in succeeded.`,
            ),
          );
          return failed('UNKNOWN');
        }
        return succeeded({ refreshToken: result.user.refreshToken, credential: result });
      },
      e => {
        // https://firebase.google.com/docs/reference/js/firebase.auth.Error
        // https://firebase.google.com/docs/reference/js/Auth.html#signinwithpopup
        switch (e.code) {
          case 'auth/user-cancelled':
            return failed('CANCELED');
          case 'auth/popup-closed-by-user':
            return failed('POPUP_CLOSED');
          case 'auth/popup-blocked':
            return failed('POPUP_BLOCKED');
          case 'auth/account-exists-with-different-credential':
            return failed('ANOTHER_ACCOUNT_EXISTS');
          default:
            errorReporter.report(e);
            return failed('UNKNOWN');
        }
      },
    );
  }

  async function loginWithRefreshToken({
    refreshToken,
    credential,
  }: {
    refreshToken: string;
    credential: any;
  }): Promise<SocialLoginErrorOr<{ credential: UserCredential }>> {
    const { loginWithRefreshToken: result } = await apolloClient
      .mutate<LoginWithRefreshTokenMutation, LoginWithRefreshTokenMutationVariables>({
        mutation: LoginWithRefreshTokenDocument,
        variables: {
          refreshToken,
          registrationTrackingData,
        },
      })
      .then(extractDataFromResponse);
    switch (result.__typename) {
      case 'LoginWithRefreshTokenSuccess':
        return succeeded({ credential });
      case 'LoginWithRefreshTokenFailure':
        errorReporter.report(new Error(`Failed to login with refresh token unexpectedly: ${result.error}`));
        return failed('UNKNOWN');
      default:
        errorReporter.report(new Error('__typename should be specified in return value'));
        return failed('UNKNOWN');
    }
  }

  return async () => {
    const firebaseLoginResult = await loginAsFirebaseUser();
    return await firebaseLoginResult.map(props => props).flatMapF(loginWithRefreshToken);
  };
}

type SendPasswordEmailError = 'USER_NOT_FOUND';
type SendPasswordEmailResult = Result<SendPasswordEmailError, undefined>;

export function useSendPasswordResetEmail(): (email: string) => Promise<SendPasswordEmailResult> {
  const firebaseApp = useFirebaseApp();

  return async email => {
    try {
      await sendPasswordResetEmail(getAuth(firebaseApp), email);

      // TODO: Should we revoke active sessions for this user here?

      return succeeded(undefined);
    } catch (e) {
      switch (e.code) {
        case 'auth/user-not-found':
          return failed('USER_NOT_FOUND');
        default:
          throw e;
      }
    }
  };
}

export function useSendLoginLinkEmail(): (email: string) => Promise<SendPasswordEmailResult> {
  const firebaseApp = useFirebaseApp();

  return async email => {
    try {
      window.localStorage.setItem('emailForSignIn', email);
      await sendSignInLinkToEmail(getAuth(firebaseApp), email, { url: document.referrer, handleCodeInApp: true });

      return succeeded(undefined);
    } catch (e) {
      switch (e.code) {
        case 'auth/user-not-found':
          return failed('USER_NOT_FOUND');
        default:
          throw e;
      }
    }
  };
}

export function useConfirmPasswordReset(): (params: {
  oobCode: string;
  newPassword: string;
}) => Promise<ResetPasswordResult> {
  const [resetPassword] = useResetPasswordMutation();

  return async ({ oobCode, newPassword }) => {
    const response = await resetPassword({
      variables: {
        input: {
          oobCode,
          newPassword,
        },
      },
    });
    const result = await extractDataFromResponse(response);
    return result.resetPassword;
  };
}

type SendEmailVerificationError = 'LOGIN_REQUIRED' | 'UNKNOWN';
export type SendEmailVerificationResult = Result<SendEmailVerificationError, undefined>;

export function useSendEmailVerification(): () => Promise<SendEmailVerificationResult> {
  const errorReporter = useErrorReporter();
  const [sendEmailVerificationMutation] = useSendEmailVerificationMutation();

  function translateError(error: GQLSendEmailVerificationError): SendEmailVerificationError {
    switch (error) {
      case GQLSendEmailVerificationError.LoginRequired:
        return 'LOGIN_REQUIRED';
    }
  }

  return () =>
    sendEmailVerificationMutation()
      .then(extractDataFromResponse)
      .then(({ sendEmailVerification }) => {
        switch (sendEmailVerification.__typename) {
          case 'SendEmailVerificationFailure':
            return failed(translateError(sendEmailVerification.error));
          default:
            return succeeded(undefined);
        }
      })
      .catch(e => {
        errorReporter.report(e);
        return failed('UNKNOWN');
      });
}
